/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types';

import React from 'react';
import styled from 'styled-components';

import styles from '../../../../styles';
import Localized from '../../../../../shared/components/Localized';
import { StatusIcon } from '../../../../components/SVGIcon';
import { Checkbox } from '../../../../components/Form';

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;

  justify-content: center;

  @media (min-width: 768px) {
    margin: 1.25rem auto;
    min-height: 149px;
    width: 592px;
    border: 1px solid #d8dde6;
    margin: 1.25rem auto;
    overflow-y: scroll;
  }
`;

const Row = styled.div`
  display: flex;
  align-self: center;

  flex-wrap: wrap;
  width: 100%;

  @media (min-width: 768px) {
    &:not(:last-child) {
      border-bottom: 1px solid #d8dde6;
    }
  }
`;

const AllHeader = styled.div`
  width: 100%;
  ${styles.fonts.headingLabelCaps}
  background-color: white;
  border-top: 1px solid #d8dde6;
  padding: 0.5rem 0.5rem 0.5rem 1rem;

  display: flex;
  justify-content: space-between;

  @media (min-width: 768px) {
    width: 25%;
    background-color: #f4f6f9;
    border-right: 1px solid #d8dde6;
    border-top: 0;
    justify-content: flex-start;
  }
`;

const SeverityHeader = styled.div`
  ${styles.fonts.headingLabel}
  @media (max-width: 767px) {
    display: none;
  }

  @media (min-width: 768px) {
    width: 25%;
    text-align: center;
    padding: 0.5rem 0;
  }
`;

const ServiceHeader = styled.div`
  width: 100%;
  ${styles.fonts.headingLabelCaps}
  background-color: #f4f6f9;
  border-top: 1px solid #d8dde6;
  border-bottom: 1px solid #d8dde6;
  padding: 0.5rem 0.5rem 0.5rem 1rem;

  display: flex;
  justify-content: space-between;

  @media (min-width: 768px) {
    width: 25%;
    border-right: 1px solid #d8dde6;
    border-top: 0;
    border-bottom: 0;
    justify-content: flex-start;
  }
`;

const OrderedCheckbox = styled(Checkbox)`
  order: 2;

  @media (min-width: 768px) {
    order: 1;
  }
`;

const OrderedLocalized = styled(Localized)`
  order: 1;

  @media (min-width: 768px) {
    order: 2;
  }
`;

const OrderedHeader = styled.span`
  order: 1;

  @media (min-width: 768px) {
    order: 2;
  }
`;
const NotificationType = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
`;

const ServiceCheckboxCell = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.5rem 0.5rem 0.5rem 1rem;

  @media (min-width: 768px) {
    justify-content: space-around;
    width: 25%;
  }
`;

const StatusIconWithMargin = styled(StatusIcon)`
  margin-right: 0.5rem;
`;

const StatusIconSmallOnly = styled(StatusIcon)`
  @media (min-width: 768px) {
    display: none;
  }
`;
const ModalIntro = styled.p`
  margin-left: 99px;
  color: rgb(112, 110, 107);
  font-family: Salesforce Sans;
  font-size: 12px;
  font-weight: normal;
  height: 15px;
  letter-spacing: 0px;
  margin-bottom: 16px;
  @media (min-width: 20rem) and (max-width: 30rem) {
    margin-left: 1.25rem;
    margin-top: 1.25rem;
  }
`;

const severities = [
  {
    key: 'major',
    localizationKey: 'disruption',
    imgUrl: '/assets/icons/icon-major.png',
  },
  {
    key: 'minor',
    localizationKey: 'degradation',
    imgUrl: '/assets/icons/icon-minor.png',
  },
  {
    key: 'maintenance',
    localizationKey: 'maintenance',
    imgUrl: '/assets/icons/icon-maintenance.png',
  },
];
export default class ServiceSeverityGrid extends React.Component {
  static propTypes = {
    instanceSubscription: PropTypes.object,
    severities: PropTypes.array,
    services: PropTypes.array,
    clickAllServices: PropTypes.func,
    clickService: PropTypes.func,
    clickServiceSeverity: PropTypes.func,
    readOnly: PropTypes.bool,
    invalid: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      subType: 'email',
    };
  }

  disableButton = type => {
    const { emailVerified, mobileNumberVerified } = this.props.requests.subscriber.value;
    return (type === 'email' && !emailVerified) || (type === 'sms' && !mobileNumberVerified);
  };

  renderNotificationTypeButton = () => {
    const { subType } = this.state;
    return ['Email', 'SMS'].map(label => {
      const type = label.toLowerCase();
      const buttonType = type === subType ? 'brand' : 'neutral';
      return (
        <button
          key={type}
          disabled={this.disableButton(type)}
          className={`slds-button slds-button_${buttonType}`}
          onClick={() => {
            return this.setState({ subType: type });
          }}
        >
          {label}
        </button>
      );
    });
  };

  render() {
    const {
      serviceSeverityGrid: {
        state,
        actions: { toggleAll, toggleService, toggleServiceSeverity },
      },
    } = this.props;

    return (
      <div>
        <ModalIntro>I would like to receive notifications via:</ModalIntro>
        <NotificationType>
          <div className="slds-button-group" role="group">
            {this.renderNotificationTypeButton()}
          </div>
        </NotificationType>

        <Grid className={this.props.className}>
          <Row>
            <AllHeader>
              {this.state.subType === 'email' && (
                <OrderedCheckbox
                  name={'allCorner'}
                  checked={state.allCheckedEmail}
                  indeterminate={state.allIndeterminate}
                  value={state.allCheckedEmail}
                  onChange={value => {
                    toggleAll(value, this.state.subType);
                  }}
                />
              )}
              {this.state.subType === 'sms' && (
                <OrderedCheckbox
                  name={'allCorner'}
                  checked={state.allCheckedSMS}
                  indeterminate={state.allIndeterminate}
                  value={state.allCheckedSMS}
                  onChange={value => {
                    toggleAll(value, this.state.subType);
                  }}
                />
              )}
              <OrderedHeader>All Services</OrderedHeader>
            </AllHeader>
            {severities.map((severity, i) => {
              return (
                <SeverityHeader key={i}>
                  <StatusIconWithMargin size="1rem" severity={severity.key} />
                  <Localized modelName={'statusKey'} modelKey={severity.localizationKey} modelAttribute={'label'} />
                </SeverityHeader>
              );
            })}
          </Row>
          {this.state.subType === 'email' &&
            state.services.map((service, i) => {
              return (
                <Row key={i}>
                  <ServiceHeader>
                    <OrderedCheckbox
                      name={`${service.serviceKey}`}
                      checked={service.checked}
                      indeterminate={service.indeterminate}
                      value={service.checked}
                      onChange={value => {
                        return toggleService(service.serviceKey, value, 'email');
                      }}
                    />
                    <OrderedLocalized modelName="service" modelKey={service.serviceKey} modelAttribute="label" />
                  </ServiceHeader>
                  {severities.map((severity, ii) => {
                    return (
                      <ServiceCheckboxCell key={i + '_' + ii}>
                        <StatusIconSmallOnly size="1rem" severity={severity.key} />
                        <Checkbox
                          name={`${service.serviceKey}-${severity.key}`}
                          id={`${service.serviceKey}-${severity.key}`}
                          checked={service[severity.key]}
                          value={service[severity.key]}
                          onChange={value => {
                            return toggleServiceSeverity(service.serviceKey, severity.key, value, this.state.subType);
                          }}
                        />
                      </ServiceCheckboxCell>
                    );
                  })}
                </Row>
              );
            })}

          {this.state.subType === 'sms' &&
            state.servicesSMS.map((service, i) => {
              return (
                <Row key={i}>
                  <ServiceHeader>
                    <OrderedCheckbox
                      name={`${service.serviceKey}`}
                      checked={service.checked}
                      indeterminate={service.indeterminate}
                      value={service.checked}
                      onChange={value => {
                        return toggleService(service.serviceKey, value, 'sms');
                      }}
                    />
                    <OrderedLocalized modelName="service" modelKey={service.serviceKey} modelAttribute="label" />
                  </ServiceHeader>
                  {severities.map((severity, ii) => {
                    return (
                      <ServiceCheckboxCell key={i + '_' + ii}>
                        <StatusIconSmallOnly size="1rem" severity={severity.key} />
                        <Checkbox
                          name={`${service.serviceKey}-${severity.key}`}
                          id={`${service.serviceKey}-${severity.key}`}
                          checked={service[severity.key]}
                          value={service[severity.key]}
                          onChange={value => {
                            return toggleServiceSeverity(service.serviceKey, severity.key, value, this.state.subType);
                          }}
                        />
                      </ServiceCheckboxCell>
                    );
                  })}
                </Row>
              );
            })}
        </Grid>
      </div>
    );
  }
}
